import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

const ModalOverlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 99999;
	padding: 20px;
`;

const ModalContainer = styled.div`
	background: white;
	border-radius: 12px;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
	width: 500px;
	max-width: calc(100vw - 40px);
	max-height: calc(100vh - 40px);
	overflow-y: auto;
	position: relative;
	z-index: 100000;
`;

const ModalHeader = styled.div`
	padding: 20px 20px 0 20px;
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

const ModalTitle = styled.h2`
	font-size: 18px;
	font-weight: 600;
	color: #111827;
	margin: 0;
`;

const CloseButton = styled.button`
	background: none;
	border: none;
	font-size: 20px;
	color: #6b7280;
	cursor: pointer;
	padding: 4px;
	&:hover {
		color: #374151;
	}
`;

const StepIndicator = styled.div`
	color: #6b7280;
	font-size: 14px;
`;

const ModalBody = styled.div`
	padding: 20px;
`;

const InputGroup = styled.div`
	margin-bottom: 20px;
`;

const AmountInput = styled.input`
	width: 100%;
	padding: 12px 16px;
	border: 1px solid #d1d5db;
	border-radius: 6px;
	font-size: 16px;
	background-color: #fef2f2;
	color: #dc2626;
	font-weight: 500;
	&:focus {
		outline: none;
		border-color: #2563EB;
		box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
	}
`;

const Label = styled.label`
	display: block;
	font-size: 14px;
	font-weight: 500;
	color: #374151;
	margin-bottom: 8px;
`;

const Required = styled.span`
	color: #dc2626;
`;

const TextArea = styled.textarea`
	width: 100%;
	padding: 12px 16px;
	border: 1px solid #d1d5db;
	border-radius: 6px;
	font-size: 14px;
	min-height: 100px;
	resize: vertical;
	&:focus {
		outline: none;
		border-color: #2563EB;
		box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
	}
	&::placeholder {
		color: #9ca3af;
	}
`;

const ModalFooter = styled.div`
	padding: 0 20px 20px 20px;
	display: flex;
	justify-content: flex-end;
	gap: 12px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
	padding: 10px 20px;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s;
	border: 1px solid;
	
	${props => props.variant === 'primary' ? `
		background-color: #2563EB;
		border-color: #2563EB;
		color: white;
		&:hover {
			background-color: #1d4ed8;
		}
	` : `
		background-color: white;
		border-color: #d1d5db;
		color: #374151;
		&:hover {
			background-color: #f9fafb;
		}
	`}
`;

const NextIcon = styled.span`
	margin-left: 8px;
`;

const ErrorMessage = styled.div`
	color: #dc2626;
	font-size: 12px;
	margin-top: 4px;
`;

interface RejectAdjustmentModalProps {
	isOpen: boolean;
	onClose: () => void;
	onNext: (data: { amount: string; reason: string }) => void;
	amount?: string;
	currency?: string;
}

export default function RejectAdjustmentModal(props: RejectAdjustmentModalProps) {
	const { isOpen, onClose, onNext, currency = 'USD' } = props;
	const amount = props.amount ?? `0.00 ${currency}`;

	const [reason, setReason] = useState('');
	const [error, setError] = useState('');

	const handleNext = () => {
		if (reason.trim()) {
			setError('');
			onNext({ amount, reason: reason.trim() });
		} else {
			setError('Vui lòng nhập lý do từ chối');
		}
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && e.ctrlKey) {
			handleNext();
		}
	};

	// Reset state khi modal đóng
	useEffect(() => {
		if (!isOpen) {
			const timer = setTimeout(() => {
				setReason('');
				setError('');
			}, 100);
			
			return () => clearTimeout(timer);
		}
	}, [isOpen]);

	if (!isOpen) return null;

	const modalContent = (
		<ModalOverlay onClick={onClose}>
			<ModalContainer onClick={(e) => e.stopPropagation()}>
				<ModalHeader>
					<div>
						<ModalTitle>Từ chối & Đề xuất điều chỉnh mới</ModalTitle>
						<StepIndicator>Bước 1/2</StepIndicator>
					</div>
					<CloseButton onClick={onClose}>×</CloseButton>
				</ModalHeader>
			
				<ModalBody>
					<InputGroup>
						<AmountInput 
							type="text" 
							value={amount} 
							readOnly 
						/>
					</InputGroup>
					
					<InputGroup>
						<Label>
							Lý do từ chối <Required>*</Required>
						</Label>
						<TextArea
							value={reason}
							onChange={(e) => {
								setReason(e.target.value);
								if (error) setError('');
							}}
							placeholder="Lý do..."
							style={{
								border: `1px solid ${error ? '#dc2626' : '#d1d5db'}`,
							}}
							onKeyDown={handleKeyPress}
						/>
						{error && <ErrorMessage>{error}</ErrorMessage>}
					</InputGroup>
				</ModalBody>
				
				<ModalFooter>
					<Button variant="secondary" onClick={onClose}>
						Hủy
					</Button>
					<Button 
						variant="primary" 
						onClick={handleNext}
					>
						Tiếp theo
						<NextIcon>→</NextIcon>
					</Button>
				</ModalFooter>
			</ModalContainer>
		</ModalOverlay>
	);

	return createPortal(modalContent, document.body);
}

